import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { useLocation, useLink } from "@grandvisionhq/frk-routing";
import { Hooks } from "@grandvisionhq/fun-orders";
import { PaymentFlow, PaymentResult } from "@grandvisionhq/payments";
import { CreateOrderErrors, CheckoutErrorsMapping } from "../../../../mappers/mappers/errors-mapping.js";
import { dispatchCartChangeEvent } from "../../../carts/hooks/carts/use-cart-change-listener.js";
import { CreateOrderError } from "../../../../models/orders/models/orders/create-order-error.js";
import { CheckoutStepErrorType } from "../../../../models/checkout/models/checkout/checkout-step-error-type.js";
import { appendQueryParamToUrl } from "../../../../utils/utils/get-updated-url-with-query-params.js";
const useCreateOrder = ({ onError, returnPath } = {}) => {
  const [paymentFlow, setPaymentFlow] = React.useState(PaymentFlow.UNDEFINED);
  const [orderResponse, setOrderResponse] = React.useState(void 0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const { cart } = Context.useCart();
  const { setContext } = Context.useCartSettings();
  const { replace } = useLocation();
  const { href: paymentHref } = useLink({
    route: "checkout.payment",
    params: {
      cartId: ""
    }
  }) || {};
  const { placeOrder } = Hooks.usePlaceOrder();
  const createOrder = React.useCallback(
    async (orderPayload) => {
      setLoading(true);
      setOrderResponse(void 0);
      const paymentMethod = orderPayload.paymentRequest.paymentMethod;
      try {
        if (!paymentMethod) {
          console.error("Need a payment method to create an order");
          throw new CreateOrderError({ status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED });
        }
        if (!cart) {
          throw new CreateOrderError({
            status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
            label: CheckoutErrorsMapping.CartNotFound
          });
        }
        setError(null);
        const placeOrderResponse = await placeOrder(orderPayload);
        setOrderResponse(placeOrderResponse);
        if (placeOrderResponse && placeOrderResponse.redirect) {
          if (placeOrderResponse.httpMethod !== "GET") {
            throw new CreateOrderError({ status: CreateOrderErrors.UNSUPPORTED_REDIRECT_METHOD });
          }
          dispatchCartChangeEvent(orderPayload.checkoutId);
          placeOrderResponse?.redirectUrl && replace(placeOrderResponse?.redirectUrl);
        } else if (placeOrderResponse?.success) {
          if (placeOrderResponse.resultType === "QRCODE") {
            setPaymentFlow(PaymentFlow.QR_CODE);
          }
          return { ...placeOrderResponse, cartId: orderPayload.checkoutId };
        } else if (!placeOrderResponse?.success && placeOrderResponse?.productPriceUpdated) {
          throw new CreateOrderError({
            status: CreateOrderErrors.PRODUCT_PRICE_UPDATED,
            cartHash: placeOrderResponse.cartHash,
            cartId: placeOrderResponse.cartId,
            type: orderPayload.paymentRequest.paymentMethod
          });
        } else if (!placeOrderResponse?.success && placeOrderResponse?.cartId) {
          throw new CreateOrderError({
            status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
            label: CheckoutErrorsMapping.SomethingWentWrong,
            cartHash: placeOrderResponse.cartHash,
            cartId: placeOrderResponse.cartId,
            type: orderPayload.paymentRequest.paymentMethod
          });
        } else {
          throw new CreateOrderError({
            status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
            label: CheckoutErrorsMapping.SomethingWentWrong,
            cartId: orderPayload.checkoutId,
            type: orderPayload.paymentRequest.paymentMethod
          });
        }
        setLoading(false);
        return placeOrderResponse;
      } catch (e) {
        console.error(`Order creation failed for ${orderPayload.checkoutId}`);
        setLoading(false);
        let isProductPriceUpdatedError = false;
        if (e instanceof CreateOrderError) {
          onError && onError(e.status, e?.label);
          if (e?.cartHash && e?.cartId) {
            setContext && setContext({ cartId: e?.cartId, cartHash: e?.cartHash });
          }
          const returnUrl = returnPath ?? paymentHref;
          if (returnUrl) {
            isProductPriceUpdatedError = e?.status === CreateOrderErrors.PRODUCT_PRICE_UPDATED;
            const status = isProductPriceUpdatedError ? CheckoutStepErrorType.ProductPriceUpdated : PaymentResult.Failed;
            replace(
              appendQueryParamToUrl(returnUrl, {
                cartHash: e?.cartHash,
                cartId: e?.cartId,
                type: e?.type?.toUpperCase(),
                status
              })
            );
          }
        }
        isProductPriceUpdatedError ? setError(CreateOrderErrors.PRODUCT_PRICE_UPDATED) : setError(CreateOrderErrors.ORDER_CREATION_CALL_FAILED);
        return { success: false, redirect: false };
      }
    },
    [cart, onError, paymentHref, placeOrder, replace, returnPath, setContext]
  );
  return React.useMemo(
    () => [
      createOrder,
      {
        loading,
        error,
        orderResponse,
        paymentFlow,
        setPaymentFlow
      }
    ],
    [createOrder, loading, error, orderResponse, paymentFlow, setPaymentFlow]
  );
};
export {
  useCreateOrder
};
//# sourceMappingURL=use-create-order.js.map
